// Birim varyantı DR4/DR5/DR6/DR8/DR10 — birim seçicinin altındaki etki bandı (anında preview).
// Bölümler (DR4): (1) kapsam satırı, (2) örtüşmeyen sayımlar, (3) DR8 zorunlu radyo ("sayılar
// hangi birimde girildi"), (4) DR6 yuvarlama örneği, (5) bilgi (bağlı formüller, rozet yok).
// Engel varsa (DR5) radyo yerine tablo Gösterge / Engel / Etkilenen / İşlem + "Yeniden kontrol
// et"; ≤720 px satır kartı (DR13). Katalog modu (DR10): kullanımdaki müşteri listesi. ARIA: üst
// rol TEK yerde (`bandRole`): engel / kullanımda → `role=alert`, diğerleri `role=status
// aria-live=polite`; iç bloklar rolsüz (iç içe canlı bölge çift duyuru üretirdi). Kaydet kapısı saf `canSave` (lib/kpi-unit-band.ts) — sahibi bileşen düğmeyi buna bağlar.

import { useId, type CSSProperties } from "react";
import type { KpiUnitChangePreview, KpiUnitUsage } from "@mahir/shared";
import { useMediaQuery } from "../../hooks/use-media-query";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import {
  hasUnitBlockers,
  inUse,
  needsUnitMode,
  type UnitChangeMode,
} from "../../lib/kpi-unit-band";
import { formatUnitFactor, formatUnitNumber } from "../../lib/kpi-unit-options";
import {
  amberBox,
  btnSmall,
  decisionText,
  helpText,
  tdStyle,
  thStyleStrong,
} from "./kpi-form-styles";

const B = KPI_UNIT_COPY.band;
export {
  canSave,
  canSaveCatalog,
  hasUnitBlockers,
  needsUnitMode,
  type UnitChangeMode,
} from "../../lib/kpi-unit-band";
type ActionKind = "openFormula" | "inspectValues" | "inspectReported" | "inspectTargets";
interface BlockerRow {
  key: string;
  label: string;
  code: string;
  blocker: string;
  detail: string | null;
  affected: string;
  actions: { kind: ActionKind; setId: string | null }[];
}

export function unitBlockerRows(
  p: KpiUnitChangePreview,
  indicator: { code: string; label: string } | undefined,
): BlockerRow[] {
  const rows: BlockerRow[] = [];
  const from = p.from ?? KPI_UNIT_COPY.unitless;
  const to = p.to ?? KPI_UNIT_COPY.unitless;
  if (!p.noop && p.factor === null && inUse(p)) {
    const actions: BlockerRow["actions"] = [];
    if (p.values > 0) actions.push({ kind: "inspectValues", setId: null });
    if (p.reportedValues > 0) actions.push({ kind: "inspectReported", setId: null });
    if (p.targets > 0) actions.push({ kind: "inspectTargets", setId: null });
    rows.push({
      key: "self",
      label: indicator?.label ?? indicator?.code ?? "—",
      code: indicator?.code ?? "",
      blocker: B.kinds.notConvertible(from, to),
      detail: KPI_UNIT_COPY.noCurrency,
      affected: B.counts(p.values, p.reportedValues, p.targets),
      actions,
    });
  }
  const setNames = new Map<string, string>();
  for (const d of p.dependentFormulas) if (d.setId && d.setName) setNames.set(d.setId, d.setName);
  p.blockers.formulaConflicts.forEach((f, i) =>
    rows.push({
      key: `f${i.toString()}`,
      label: f.code,
      code: f.code,
      blocker: B.kinds.formulaConflict(from),
      detail: f.expression,
      affected: B.affectedFormula(f.setId ? (setNames.get(f.setId) ?? null) : null),
      actions: [{ kind: "openFormula", setId: f.setId }],
    }),
  );
  p.blockers.dependents.forEach((d, i) => {
    const actions: BlockerRow["actions"] = [];
    if (d.reported > 0) actions.push({ kind: "inspectReported", setId: null });
    if (d.targets > 0) actions.push({ kind: "inspectTargets", setId: null });
    rows.push({
      key: `d${i.toString()}`,
      label: d.label,
      code: d.code,
      blocker: B.kinds.dependent,
      detail: null,
      affected: B.affectedDependent(d.reported, d.targets),
      actions,
    });
  });
  return rows;
}

export interface UnitChangeBandProps {
  preview: KpiUnitChangePreview | null;
  loading: boolean;
  error: boolean;
  mode: UnitChangeMode | null;
  onMode: (mode: UnitChangeMode) => void;
  onRecheck: () => void;
  clientName: string;
  variant: "indicator" | "catalog";
  /** DR10 — katalog modunda müşteri bazında kullanım */
  catalogUsage?: KpiUnitUsage | null;
  /** engel tablosunda "bu gösterge" satırı (TL → USD çevrilemez) */
  indicator?: { code: string; label: string };
  /** DR8 radyo örneği için gerçek hücre değeri; yoksa yuvarlama örneği kullanılır */
  sampleValue?: number | null;
  onOpenFormula?: (code: string, setId: string | null) => void;
  onInspectValues?: (code: string) => void;
  onInspectReported?: (code: string) => void;
  onInspectTargets?: (code: string) => void;
  /** TODO-KPI-96: katalog bandı — müşteri + (varsa) o müşterinin göstergeyi içeren ilk veri seti */
  onOpenClient?: (clientId: string, setId?: string | null) => void;
}

/**
 * Bandın üst düzey ARIA rolü (TODO-KPI-102): engel / kullanımda bildirimi `alert`, geri kalanı
 * `status`. Rol TEK yerde — içteki bloklar rolsüz (iç içe live region çift duyuru üretirdi).
 */
export function bandRole(
  p: Pick<UnitChangeBandProps, "preview" | "loading" | "error" | "variant" | "catalogUsage">,
): "alert" | "status" {
  if (p.loading || p.error) return "status";
  if (p.variant === "catalog")
    return (p.catalogUsage?.clients.length ?? 0) > 0 ? "alert" : "status";
  return p.preview && !p.preview.noop && hasUnitBlockers(p.preview) ? "alert" : "status";
}

export function UnitChangeBand(props: UnitChangeBandProps): JSX.Element | null {
  const { preview: p, loading, error, mode, onMode, onRecheck } = props;
  const narrow = useMediaQuery("(max-width: 720px)");
  const radioName = useId();

  let body: JSX.Element | null = null;
  if (loading) {
    body = (
      <div aria-busy="true" style={{ display: "grid", gap: 6 }}>
        <span style={helpText}>{B.loading}</span>
        <span aria-hidden="true" style={{ ...skeleton, width: "70%" }} />
        <span aria-hidden="true" style={{ ...skeleton, width: "45%" }} />
      </div>
    );
  } else if (error) {
    body = (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <span style={{ ...decisionText, color: "var(--color-error)" }}>⚠ {B.error}</span>
        <button type="button" style={btnSmall} onClick={onRecheck}>
          {B.retry}
        </button>
      </div>
    );
  } else if (props.variant === "catalog") {
    body = <CatalogUsage {...props} narrow={narrow} />;
  } else if (p && !p.noop) {
    body = (
      <IndicatorImpact
        {...props}
        preview={p}
        narrow={narrow}
        radioName={radioName}
        mode={mode}
        onMode={onMode}
      />
    );
  }
  if (body === null) return null;
  return (
    <section
      role={bandRole(props)}
      {...(bandRole(props) === "status" ? { "aria-live": "polite" as const } : {})}
      style={bandStyle}
    >
      {body}
    </section>
  );
}

function IndicatorImpact(
  props: UnitChangeBandProps & {
    preview: KpiUnitChangePreview;
    narrow: boolean;
    radioName: string;
  },
): JSX.Element {
  const { preview: p, clientName, mode, onMode, narrow, radioName } = props;
  const from = p.from ?? KPI_UNIT_COPY.unitless;
  const to = p.to ?? KPI_UNIT_COPY.unitless;
  const years = p.yearRange
    ? p.yearRange.min === p.yearRange.max
      ? p.yearRange.min.toString()
      : `${p.yearRange.min.toString()}–${p.yearRange.max.toString()}`
    : null;
  const blocked = hasUnitBlockers(p);
  const k = p.factor;
  // TODO-KPI-96: önizlemeden gelen gerçek hücre (işletme + yıl) — yoksa yuvarlama örneği
  const smp = props.sampleValue == null ? (p.sample ?? null) : null;
  const raw = props.sampleValue ?? smp?.value ?? p.lossySample?.raw ?? null;
  const origin = smp ? ` · ${smp.entityName}, ${smp.year.toString()}` : "";
  const convertedRaw = smp ? smp.converted : raw !== null && k !== null ? raw * k : null;
  const convertSample =
    raw !== null && convertedRaw !== null
      ? `${formatUnitNumber(raw)} ${from} → ${formatUnitNumber(convertedRaw)} ${to}${origin}`
      : null;
  const relabelSample =
    raw !== null
      ? `${formatUnitNumber(raw)} ${from} → ${formatUnitNumber(raw)} ${to}${origin}`
      : null;
  const ls = p.lossySample;
  const lossySample = ls
    ? B.lossySample(
        `${formatUnitNumber(ls.raw)} ${from}`,
        `${formatUnitNumber(ls.rounded)} ${to}`,
        formatUnitNumber(ls.exact),
        ls.pct >= 1
          ? Math.round(ls.pct).toString()
          : formatUnitNumber(Math.round(ls.pct * 10) / 10),
      )
    : null;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={helpText}>{B.scope(clientName, from, to, p.setsAffected)}</div>
      {inUse(p) ? (
        <div>
          <div style={decisionText}>{B.counts(p.values, p.reportedValues, p.targets)}</div>
          {years || p.entities > 0 ? <div style={helpText}>{B.span(years, p.entities)}</div> : null}
        </div>
      ) : (
        <div style={decisionText}>{B.none}</div>
      )}
      {blocked ? (
        <Blockers {...props} narrow={narrow} />
      ) : (
        <>
          {needsUnitMode(p) && k !== null ? (
            <fieldset style={{ border: 0, margin: 0, padding: 0, display: "grid", gap: 6 }}>
              <legend style={{ ...decisionText, fontWeight: 600, marginBottom: 4 }}>
                {B.radioLegend}
              </legend>
              <label style={radioRow}>
                <input
                  type="radio"
                  name={radioName}
                  value="convert"
                  required
                  checked={mode === "convert"}
                  onChange={() => onMode("convert")}
                />
                <span style={decisionText}>
                  {B.radioConvert(from, formatUnitFactor(k), convertSample)}
                </span>
              </label>
              <label style={radioRow}>
                <input
                  type="radio"
                  name={radioName}
                  value="relabel"
                  required
                  checked={mode === "relabel"}
                  onChange={() => onMode("relabel")}
                />
                <span style={decisionText}>{B.radioRelabel(to, relabelSample)}</span>
              </label>
              {mode === null ? <span style={helpText}>{B.radioRequired}</span> : null}
            </fieldset>
          ) : null}
          {p.lossyCount > 0 && mode !== "relabel" ? (
            <div style={{ ...amberBox, fontSize: "0.875rem" }}>
              ⚠ {B.lossy(p.lossyCount, lossySample)}
            </div>
          ) : null}
          {p.dependentFormulas.length > 0 ? (
            <div style={helpText}>
              ⓘ {B.dependentFormulas(p.dependentFormulas.length)}:{" "}
              {p.dependentFormulas.map((f) => f.code).join(", ")}
            </div>
          ) : null}
          {inUse(p) ? <div style={helpText}>ⓘ {B.noCorrectionBadge}</div> : null}
        </>
      )}
    </div>
  );
}

function actionHandler(
  props: UnitChangeBandProps,
  kind: ActionKind,
  code: string,
  setId: string | null,
) {
  return (): void => {
    if (kind === "openFormula") props.onOpenFormula?.(code, setId);
    else if (kind === "inspectValues") props.onInspectValues?.(code);
    else if (kind === "inspectReported") props.onInspectReported?.(code);
    else props.onInspectTargets?.(code);
  };
}

function Blockers(
  props: UnitChangeBandProps & { preview: KpiUnitChangePreview; narrow: boolean },
): JSX.Element {
  const rows = unitBlockerRows(props.preview, props.indicator);
  const actions = (r: BlockerRow): JSX.Element => (
    <span style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {r.actions.map((a) => (
        <button
          key={a.kind}
          type="button"
          style={linkBtn}
          onClick={actionHandler(props, a.kind, r.code, a.setId)}
        >
          {B.actions[a.kind]}
        </button>
      ))}
    </span>
  );
  const name = (r: BlockerRow): JSX.Element => (
    <span>
      <span style={{ fontWeight: 600 }}>{r.label}</span>
      {r.code && r.code !== r.label ? <span style={codeHint}> {r.code}</span> : null}
    </span>
  );
  const blocker = (r: BlockerRow): JSX.Element => (
    <span>
      {r.blocker}
      {r.detail ? <span style={{ ...helpText, display: "block" }}>{r.detail}</span> : null}
    </span>
  );
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ ...decisionText, fontWeight: 700, color: "var(--color-warning-text)" }}>
        ⛔ {B.blockedTitle}
      </div>
      {props.narrow ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {rows.map((r) => (
            <li key={r.key} style={cardStyle}>
              <div style={decisionText}>{name(r)}</div>
              <div style={helpText}>
                {B.cols.blocker}: {blocker(r)}
              </div>
              <div style={helpText}>
                {B.cols.affected}: {r.affected}
              </div>
              {actions(r)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="kpi-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[B.cols.indicator, B.cols.blocker, B.cols.affected, B.cols.action].map((h) => (
                  <th key={h} scope="col" style={{ ...thStyleStrong, fontSize: "0.875rem" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td style={cell}>{name(r)}</td>
                  <td style={cell}>{blocker(r)}</td>
                  <td style={cell}>{r.affected}</td>
                  <td style={cell}>{actions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={helpText}>{B.blockedPaths}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button type="button" style={btnSmall} onClick={props.onRecheck}>
          ↻ {B.recheck}
        </button>
        <span style={helpText}>{B.revert}</span>
      </div>
    </div>
  );
}

function CatalogUsage(props: UnitChangeBandProps & { narrow: boolean }): JSX.Element | null {
  const C = KPI_UNIT_COPY.catalog;
  const clients = props.catalogUsage?.clients ?? [];
  if (clients.length === 0) return <div style={decisionText}>{B.none}</div>;
  const open = (id: string, setId: string | null): JSX.Element => (
    <button type="button" style={linkBtn} onClick={() => props.onOpenClient?.(id, setId)}>
      {C.open}
    </button>
  );
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ ...decisionText, fontWeight: 700, color: "var(--color-warning-text)" }}>
        ⛔ {C.inUse(clients.length)}
      </div>
      {props.narrow ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {clients.map((c) => (
            <li key={c.id} style={cardStyle}>
              <div style={{ ...decisionText, fontWeight: 600 }}>{c.name}</div>
              <div style={helpText}>
                {C.usage(c.values, c.targets)} · {c.unit ?? KPI_UNIT_COPY.unitless}
              </div>
              {open(c.id, c.setIds[0] ?? null)}
            </li>
          ))}
        </ul>
      ) : (
        <div className="kpi-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[C.cols.client, C.cols.usage, C.cols.unit, B.cols.action].map((h) => (
                  <th key={h} scope="col" style={{ ...thStyleStrong, fontSize: "0.875rem" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td style={{ ...cell, fontWeight: 600 }}>{c.name}</td>
                  <td style={cell}>{C.usage(c.values, c.targets)}</td>
                  <td style={cell}>{c.unit ?? KPI_UNIT_COPY.unitless}</td>
                  <td style={cell}>{open(c.id, c.setIds[0] ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={helpText}>{C.note}</div>
    </div>
  );
}

const bandStyle: CSSProperties = {
  marginTop: 8,
  padding: "12px 14px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-bg)",
  fontFamily: "var(--font-sans)",
  maxHeight: "60vh",
  overflowY: "auto",
};
const skeleton: CSSProperties = {
  display: "block",
  height: 12,
  borderRadius: 6,
  background: "color-mix(in srgb, var(--color-text-3) 18%, transparent)",
};
const radioRow: CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  cursor: "pointer",
};
const cell: CSSProperties = { ...tdStyle, fontSize: "0.875rem", verticalAlign: "top" };
const codeHint: CSSProperties = {
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: "0.875rem",
  color: "var(--color-text-2)",
};
const linkBtn: CSSProperties = {
  background: "transparent",
  border: 0,
  padding: "4px 0",
  minHeight: 32,
  color: "var(--color-primary)",
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: "0.875rem",
  fontFamily: "var(--font-sans)",
};
const cardStyle: CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  padding: "10px 12px",
  background: "var(--color-surface)",
  display: "grid",
  gap: 4,
};
